import { drynessScoreFromRainfall, sunshineScoreFromHours } from "../lib/scoring.js";
import { useState, useMemo } from "react"
import { scoreTown } from "../lib/scoring.js"
import { CRITERIA } from "../lib/criteria.js"

// Categoriegroepen voor deelscore
const GROUPS = [
  { key: "gezin",     label: "🏡 Gezin",     ids: ["youngFamilies", "montessori", "forestSchool", "services"] },
  { key: "klimaat",   label: "☀️ Klimaat",    ids: ["climate", "dryness", "sunshine"] },
  { key: "natuur",    label: "🌿 Natuur",     ids: ["natureAccess", "tranquility"] },
  { key: "lifestyle", label: "☕ Lifestyle",  ids: ["coffee", "wellness", "surf", "culturalAuthenticity", "foodAutonomy"] },
]

function groupScore(town, concelho, group, weights) {
  const criteriaMap = Object.fromEntries((CRITERIA || []).map(c => [c.id, c]))
  let wsum = 0, wscore = 0
  for (const id of group.ids) {
    const c = criteriaMap[id]
    if (!c) continue
    const w = weights?.[id] ?? c.defaultWeight ?? 5
    if (w === 0) continue
    let score = town.scores?.[id]

    // Klimaat-criteria: bereken uit concelho data
    if (id === "dryness" && concelho?.climate?.annual_rainfall_mm) {
      const mm = concelho.climate.annual_rainfall_mm
      score = drynessScoreFromRainfall(mm)
    } else if (id === "sunshine" && concelho?.climate?.annual_sunshine_h) {
      const h = concelho.climate.annual_sunshine_h
      score = h >= 3000 ? 5 : h >= 2700 ? 4 : h >= 2400 ? 3 : h >= 2100 ? 2 : 1
    } else if (id === "climate" && concelho?.climate) {
      const hd = concelho.climate.heat_days_35 ?? 0
      score = hd <= 5 ? 5 : hd <= 15 ? 4 : hd <= 25 ? 3 : hd <= 35 ? 2 : 1
    }

    if (score == null) continue
    wsum   += w
    wscore += score * w
  }
  if (!wsum) return null
  return Math.round(wscore / wsum / 5 * 100)
}

function generateSummary(scored, bests, weights) {
  if (!scored || scored.length < 2) return null

  const sentences = []

  // ── 1. Winner sentence ──────────────────────────────────────────────────
  const winner = scored.reduce((a, b) => a.pct >= b.pct ? a : b)
  if (winner) {
    // Find what the winner is best at (highest category score)
    const catScores = GROUPS.map(g => ({
      label: g.label.replace(/^[^ ]+ /, "").toLowerCase(), // strip emoji
      val: groupScore(winner.t, winner.c, g, weights),
    })).filter(g => g.val != null)
    catScores.sort((a, b) => b.val - a.val)
    const topCats = catScores.slice(0, 2).map(g => `${g.label} (${g.val}%)`)
    const catStr = topCats.length ? ` en scoort het best op ${topCats.join(" en ")}` : ""
    sentences.push(
      <p key="winner">
        🏆 <strong>{winner.t.name}</strong> wint overall ({winner.pct}){catStr}.
      </p>
    )
  }

  // ── 2. Climate sentence (best sun + lowest rain) ─────────────────────────
  const bestSun  = scored.reduce((a, b) => {
    const va = a.c?.climate?.annual_sunshine_h ?? 0
    const vb = b.c?.climate?.annual_sunshine_h ?? 0
    return va >= vb ? a : b
  })
  const bestRain = scored.reduce((a, b) => {
    const va = a.c?.climate?.annual_rainfall_mm ?? Infinity
    const vb = b.c?.climate?.annual_rainfall_mm ?? Infinity
    return va <= vb ? a : b
  })
  const sunH  = bestSun.c?.climate?.annual_sunshine_h
  const rainMm = bestRain.c?.climate?.annual_rainfall_mm
  if (sunH != null || rainMm != null) {
    const sameCity = bestSun.t.name === bestRain.t.name
    const cityName = sameCity ? bestSun.t.name : bestSun.t.name
    const parts = []
    if (sunH != null)  parts.push(`meeste zon (${sunH} h/jr)`)
    if (rainMm != null && sameCity) parts.push(`minste neerslag (${rainMm} mm)`)
    sentences.push(
      <p key="climate">
        ☀️ <strong>{cityName}</strong> heeft het beste klimaat:{" "}
        {parts.join(" en ")}.
        {!sameCity && rainMm != null && (
          <> <strong>{bestRain.t.name}</strong> heeft de minste neerslag ({rainMm} mm).</>
        )}
      </p>
    )
  }

  // ── 3. Demographics sentence (youngest population = lowest aging index) ──
  const hasDemog = scored.some(x => x.c?.aging_index != null)
  if (hasDemog) {
    const youngest = scored.reduce((a, b) => {
      const va = a.c?.aging_index ?? Infinity
      const vb = b.c?.aging_index ?? Infinity
      return va <= vb ? a : b
    })
    const ai = youngest.c?.aging_index
    if (ai != null) {
      sentences.push(
        <p key="demog">
          👨‍👩‍👧 <strong>{youngest.t.name}</strong> heeft de jongste bevolking{" "}
          (aging index {ai}).
        </p>
      )
    }
  }

  if (!sentences.length) return null
  return <div>{sentences.slice(0, 3)}</div>
}



export function ComparePanel({ towns, concelhoBySlug, weights, onRemove, onOpenTown }) {
  const [open, setOpen] = useState(false)

  const scored = useMemo(() => towns.map(t => {
    const c = concelhoBySlug?.[t.concelho]
    const { pct } = scoreTown(t, weights, c)
    return { t, c, pct: Math.round(pct) }
  }).sort((a, b) => b.pct - a.pct), [towns, concelhoBySlug, weights])

  const vals   = (fn) => scored.map(fn).filter(v => v != null && !isNaN(v))
  const minOf  = (fn) => { const v = vals(fn); return v.length ? Math.min(...v) : null }
  const maxOf  = (fn) => { const v = vals(fn); return v.length ? Math.max(...v) : null }
  const isBest = (v, best) => v != null && !isNaN(v) && best != null && v === best && scored.length > 1

  const bests = useMemo(() => ({
    pct:    maxOf(x => x.pct),
    rain:   minOf(x => x.c?.climate?.annual_rainfall_mm),
    sun:    maxOf(x => x.c?.climate?.annual_sunshine_h),
    heat:   minOf(x => x.c?.climate?.heat_days_35),
    sea:    minOf(x => x.t.hardRequirements?.seaDistanceMin),
    price:  minOf(x => x.t.pricePerM2),
    air:    minOf(x => x.t.nearestAirport?.driveMin),
    fire:   minOf(x => x.c?.fire_risk_class),
    surf:   maxOf(x => x.t.scores?.surf),
    aging:  minOf(x => x.c?.aging_index),
    gezin:  maxOf(x => groupScore(x.t, x.c, GROUPS[0], weights)),
    klimaat:maxOf(x => groupScore(x.t, x.c, GROUPS[1], weights)),
    natuur: maxOf(x => groupScore(x.t, x.c, GROUPS[2], weights)),
    lifestyle: maxOf(x => groupScore(x.t, x.c, GROUPS[3], weights)),
    cafes:  maxOf(x => x.t.scores?._coffee_osm?.cafe),
    cowork: maxOf(x => x.t.scores?._coffee_osm?.cowork),
    market: maxOf(x => x.t.scores?._foodAutonomy_osm?.market),
    hosp:   maxOf(x => x.t.scores?._services_osm?.hospital),
    beach:  maxOf(x => x.t.scores?._natureAccess_osm?.beach),
  }), [scored, weights])

  const hasAirport = scored.some(x => x.t.nearestAirport)
  const hasOsm = scored.some(x => x.t.scores?._coffee_osm != null)

  const ScoreBar = ({ val, best }) => {
    if (val == null) return <span className="cp-na">—</span>
    const isTop = isBest(val, best)
    return (
      <div className={`cp-bar-wrap${isTop ? " cp-bar-best" : ""}`}>
        <div className="cp-bar-track">
          <div className="cp-bar-fill" style={{ width: `${val}%` }} />
        </div>
        <span className="cp-bar-pct">{val}%</span>
      </div>
    )
  }

  const Cell = ({ v, best, unit, lower }) => {
    const hi = isBest(v, best)
    if (v == null) return <td className="cp-td"><span className="cp-na">—</span></td>
    return (
      <td className={`cp-td${hi ? " cp-best" : ""}`}>
        {v}{unit && <span className="cp-unit"> {unit}</span>}
      </td>
    )
  }

  if (!towns || towns.length === 0) return null
  return (
    <>
      <button className="cp-trigger" onClick={() => setOpen(true)}>
        <span className="cp-trigger-icon">⚖</span>
        Vergelijk ({towns.length})
      </button>

      {open && (
        <div className="cp-backdrop" onClick={() => setOpen(false)}>
          <div className="cp-modal" onClick={e => e.stopPropagation()}>

            <div className="cp-header">
              <span className="cp-title">Vergelijking — {scored.length} steden</span>
              <div className="cp-header-actions">
                <button className="cp-clear" onClick={() => scored.forEach(x => onRemove(x.t.name))}>
                  Wis alles
                </button>
                <button className="cp-close" onClick={() => setOpen(false)}>✕</button>
              </div>
            </div>

            <div className="cp-body">
                            {/* ── Samenvatting ── */}
              <div className="cp-summary">
                {generateSummary(scored, bests, weights)}
              </div>

              <table className="cp-table">
                <thead>
                  <tr className="cp-thead-row">
                    <th className="cp-th-label"></th>
                    {scored.map(({ t, pct }) => (
                      <th key={t.name} className="cp-th-town">
                        <button className="cp-town-btn" onClick={() => { setOpen(false); onOpenTown?.(t) }}>
                          {t.name}
                        </button>
                        <div className={`cp-score ${pct >= 80 ? "hi" : pct >= 65 ? "mid" : "lo"}`}>
                          {pct}
                        </div>
                        <button className="cp-rem" onClick={() => onRemove(t.name)}>✕</button>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {/* ── LOCATIE ─────────────────────────────────── */}
                  <tr className="cp-section-hdr">
                    <td colSpan={scored.length + 1}>📍 Locatie</td>
                  </tr>
                  <tr>
                    <td className="cp-td-label">Regio</td>
                    {scored.map(({ t }) => <td key={t.name} className="cp-td cp-td-plain">{t.region || "—"}</td>)}
                  </tr>
                  <tr>
                    <td className="cp-td-label">Zee (min)</td>
                    {scored.map(({ t }) => {
                      const v = t.hardRequirements?.seaDistanceMin
                      return <td key={t.name} className={`cp-td${isBest(v, bests.sea) ? " cp-best" : ""}`}>{v != null ? <>{v}<span className="cp-unit"> min</span></> : <span className="cp-na">—</span>}</td>
                    })}
                  </tr>
                  <tr>
                    <td className="cp-td-label">Vastgoed/m²</td>
                    {scored.map(({ t }) => {
                      const v = t.pricePerM2
                      return <td key={t.name} className={`cp-td${isBest(v, bests.price) ? " cp-best" : ""}`}>
                        {v != null ? <>{v.toLocaleString("nl-NL")}<span className="cp-unit"> €/m²</span></> : <span className="cp-na">—</span>}
                      </td>
                    })}
                  </tr>
                  {hasAirport && (
                    <tr>
                      <td className="cp-td-label">Luchthaven</td>
                      {scored.map(({ t }) => {
                        const a = t.nearestAirport
                        return (
                          <td key={t.name} className={`cp-td${isBest(a?.driveMin, bests.air) ? " cp-best" : ""}`}>
                            {a ? <><span className="cp-air-code">{a.code}</span><span className="cp-sub"> {a.driveMin} min</span></> : <span className="cp-na">—</span>}
                          </td>
                        )
                      })}
                    </tr>
                  )}

                  {/* ── SCORES PER CATEGORIE ─────────────────────── */}
                  <tr className="cp-section-hdr">
                    <td colSpan={scored.length + 1}>📊 Scores per categorie</td>
                  </tr>
                  {GROUPS.map(group => (
                    <tr key={group.key}>
                      <td className="cp-td-label">{group.label}</td>
                      {scored.map(({ t, c }) => {
                        const v = groupScore(t, c, group, weights)
                        const best = bests[group.key]
                        return (
                          <td key={t.name} className="cp-td cp-td-bar">
                            <ScoreBar val={v} best={best} />
                          </td>
                        )
                      })}
                    </tr>
                  ))}

                  {/* ── KLIMAAT ─────────────────────────────────── */}
                  <tr className="cp-section-hdr">
                    <td colSpan={scored.length + 1}>🌤 Klimaat details</td>
                  </tr>
                  <tr>
                    <td className="cp-td-label">Neerslag</td>
                    {scored.map(({ t, c }) => {
                      const v = c?.climate?.annual_rainfall_mm
                      return <td key={t.name} className={`cp-td${isBest(v, bests.rain) ? " cp-best" : ""}`}>{v != null ? <>{v}<span className="cp-unit"> mm</span></> : <span className="cp-na">—</span>}</td>
                    })}
                  </tr>
                  <tr>
                    <td className="cp-td-label">Zon</td>
                    {scored.map(({ t, c }) => {
                      const v = c?.climate?.annual_sunshine_h
                      return <td key={t.name} className={`cp-td${isBest(v, bests.sun) ? " cp-best" : ""}`}>{v != null ? <>{v}<span className="cp-unit"> h/jr</span></> : <span className="cp-na">—</span>}</td>
                    })}
                  </tr>
                  <tr>
                    <td className="cp-td-label">Hittedagen &gt;35°</td>
                    {scored.map(({ t, c }) => {
                      const v = c?.climate?.heat_days_35
                      return <td key={t.name} className={`cp-td${isBest(v, bests.heat) ? " cp-best" : ""}`}>{v != null ? <>{v}<span className="cp-unit"> d/jr</span></> : <span className="cp-na">—</span>}</td>
                    })}
                  </tr>
                  <tr>
                    <td className="cp-td-label">Brandrisico</td>
                    {scored.map(({ t, c }) => {
                      const v = c?.fire_risk_class
                      return <td key={t.name} className={`cp-td${isBest(v, bests.fire) ? " cp-best" : ""}`}>{v != null ? <>{v}<span className="cp-unit">/5</span></> : <span className="cp-na">—</span>}</td>
                    })}
                  </tr>

                  {/* ── DEMOGRAFIE ───────────────────────────────── */}
                  <tr className="cp-section-hdr">
                    <td colSpan={scored.length + 1}>👨‍👩‍👧 Demografie</td>
                  </tr>
                  <tr>
                    <td className="cp-td-label">Jonge gezinnen</td>
                    {scored.map(({ t, c }) => {
                      const v = t.scores?.youngFamilies
                      const best = maxOf(x => x.t.scores?.youngFamilies)
                      return <td key={t.name} className={`cp-td${isBest(v, best) ? " cp-best" : ""}`}>{v != null ? <>{v}<span className="cp-unit">/5</span></> : <span className="cp-na">—</span>}</td>
                    })}
                  </tr>
                  <tr>
                    <td className="cp-td-label">Aging index</td>
                    {scored.map(({ t, c }) => {
                      const v = c?.aging_index
                      return (
                        <td key={t.name} className={`cp-td${isBest(v, bests.aging) ? " cp-best" : ""}`}>
                          {v != null
                            ? <>{v}<span className="cp-unit cp-aging-hint"> {v < 150 ? "jong" : v < 250 ? "gemengd" : "vergrijsd"}</span></>
                            : <span className="cp-na">—</span>}
                        </td>
                      )
                    })}
                  </tr>

                  {/* ── VOORZIENINGEN (OSM) ───────────────────────── */}
                  {hasOsm && (
                    <>
                      <tr className="cp-section-hdr">
                        <td colSpan={scored.length + 1}>🏪 Voorzieningen (OSM)</td>
                      </tr>
                      <tr>
                        <td className="cp-td-label">Cafés & bars</td>
                        {scored.map(({ t }) => {
                          const v = t.scores?._coffee_osm?.cafe
                          return <td key={t.name} className={`cp-td${isBest(v, bests.cafes) ? " cp-best" : ""}`}>{v != null ? v : <span className="cp-na">—</span>}</td>
                        })}
                      </tr>
                      <tr>
                        <td className="cp-td-label">Coworking</td>
                        {scored.map(({ t }) => {
                          const v = t.scores?._coffee_osm?.cowork
                          return <td key={t.name} className={`cp-td${isBest(v, bests.cowork) ? " cp-best" : ""}`}>{v != null ? v : <span className="cp-na">—</span>}</td>
                        })}
                      </tr>
                      <tr>
                        <td className="cp-td-label">Markten & boerenwinkel</td>
                        {scored.map(({ t }) => {
                          const mkt = t.scores?._foodAutonomy_osm?.market ?? 0
                          const farm = t.scores?._foodAutonomy_osm?.farm_shop ?? 0
                          const v = mkt + farm > 0 ? mkt + farm : null
                          return <td key={t.name} className={`cp-td${isBest(v, bests.market) ? " cp-best" : ""}`}>
                            {v != null
                              ? <span title={`${mkt} markt · ${farm} boerenwinkel`}>{v}</span>
                              : <span className="cp-na">—</span>}
                          </td>
                        })}
                      </tr>
                      <tr>
                        <td className="cp-td-label">Ziekenhuizen</td>
                        {scored.map(({ t }) => {
                          const v = t.scores?._services_osm?.hospital
                          return <td key={t.name} className={`cp-td${isBest(v, bests.hosp) ? " cp-best" : ""}`}>{v != null ? v : <span className="cp-na">—</span>}</td>
                        })}
                      </tr>
                      <tr>
                        <td className="cp-td-label">Stranden (OSM)</td>
                        {scored.map(({ t }) => {
                          const v = t.scores?._natureAccess_osm?.beach
                          return <td key={t.name} className={`cp-td${isBest(v, bests.beach) ? " cp-best" : ""}`}>{v != null ? v : <span className="cp-na">—</span>}</td>
                        })}
                      </tr>
                    </>
                  )}

                </tbody>
              </table>
            </div>

            <div className="cp-footer">
              Groen = beste waarde in de vergelijking · Klik stadsnaam voor details
            </div>
          </div>
        </div>
      )}
    </>
  )
}
